import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useEffect} from "react";
import type {OnBoardingStatus} from "../../authentication/authenticationQueries.js";
import {useIsAuthenticated} from "../../authentication/authenticationQueries.js";
import {useGoogleInfo} from "../../googleQueries.js";
import type {ChangePasswordRequest, ResetPasswordFinishRequest} from "./accountApi.service.js";
import {accountApiService} from "./accountApi.service.js";

export const accountKeysStore = {
  root: ["account"] as const,
  account: () => [...accountKeysStore.root, "get"] as const,
  onboardingStatus: () => [...accountKeysStore.root, "onboardingStatus"] as const,
  changePassword: () => [...accountKeysStore.root, "password", "change"] as const,
  resetPasswordInit: () => [...accountKeysStore.root, "password", "reset-init"] as const,
  resetPasswordFinish: () => [...accountKeysStore.root, "password", "reset-finish"] as const,
};

export function useAccount() {
  const {isAuthenticated} = useIsAuthenticated();
  const {
    data: account,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery({
    queryKey: accountKeysStore.account(),
    queryFn: accountApiService.getAccount,
    enabled: isAuthenticated,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    account,
    isSessionLoading: isLoading,
    isSessionFetching: isFetching,
    sessionError: error,
    refetchSession: refetch,
  };
}

function useDefaultOnboardingStatus() {
  const {account} = useAccount();
  const {googleInfo} = useGoogleInfo();
  let initialData: OnBoardingStatus | undefined = undefined;
  if (account && googleInfo) {
    if (!account.onboarding) {
      initialData = "FINISHED";
    } else {
      const hasGoogleCalendar = googleInfo.userinfo && googleInfo.hasGoogleCalendar;
      if (hasGoogleCalendar) {
        initialData = "FEATURES2";
      } else {
        initialData = "FEATURES";
      }
    }
  }
  return initialData;
}

export function useOnboardingStatus() {
  const queryClient = useQueryClient();
  const initialData = useDefaultOnboardingStatus();
  const {data: status} = useQuery({
    queryKey: accountKeysStore.onboardingStatus(),
    staleTime: Number.POSITIVE_INFINITY, // this state is master in memory, not server state that can change
    enabled: initialData !== undefined,
    queryFn() {
      // This should never return undefined because query is disabled when initialData is undefined
      return initialData;
    },
  });

  useEffect(() => {
    if (initialData !== undefined) {
      const queryKey = accountKeysStore.onboardingStatus();
      if (queryClient.getQueryData(queryKey) === undefined) {
        queryClient.setQueryData(queryKey, initialData);
      }
    }
  }, [initialData, queryClient]);
  return {status, setOnboardingStatus: handleSetOnboardingStatus};

  function handleSetOnboardingStatus(status: OnBoardingStatus) {
    const queryKey = accountKeysStore.onboardingStatus();
    queryClient.setQueryData(queryKey, status);
  }
}

export function useChangePassword() {
  const {mutateAsync, isPending, error, isSuccess, isError, reset} = useMutation({
    async mutationFn(request: ChangePasswordRequest) {
      await accountApiService.changePassword(request);
    },
    onError(error) {
      console.error("Failed to change password:", error);
    },
  });

  return {
    changePassword: mutateAsync,
    isChangingPassword: isPending,
    changePasswordError: error,
    changePasswordSuccess: isSuccess,
    changePasswordFailure: isError,
    resetChangePassword: reset,
  };
}

export function useResetPasswordInit() {
  const {mutateAsync, isPending, error, isSuccess, isError, reset} = useMutation({
    async mutationFn(email: string) {
      await accountApiService.resetPasswordInit(email);
    },
    onError(error) {
      console.error("Failed to initiate password reset:", error);
    },
  });

  return {
    resetPasswordInit: mutateAsync,
    isResetPasswordInitPending: isPending,
    resetPasswordInitError: error,
    resetPasswordInitSuccess: isSuccess,
    resetPasswordInitFailure: isError,
    resetResetPasswordInit: reset,
  };
}

export function useResetPasswordFinish() {
  const {mutateAsync, isPending, error, isSuccess, isError, reset} = useMutation({
    async mutationFn(request: ResetPasswordFinishRequest) {
      await accountApiService.resetPasswordFinish(request);
    },
    onError(error) {
      console.error("Failed to finish password reset:", error);
    },
  });

  return {
    resetPasswordFinish: mutateAsync,
    isResetPasswordFinishPending: isPending,
    resetPasswordFinishError: error,
    resetPasswordFinishSuccess: isSuccess,
    resetPasswordFinishFailure: isError,
    resetResetPasswordFinish: reset,
  };
}

// Combined hook for password reset operations
export function usePasswordReset() {
  const resetInit = useResetPasswordInit();
  const resetFinish = useResetPasswordFinish();

  return {
    // Init operations
    resetPasswordInit: resetInit.resetPasswordInit,
    isResetPasswordInitPending: resetInit.isResetPasswordInitPending,
    resetPasswordInitError: resetInit.resetPasswordInitError,
    resetPasswordInitSuccess: resetInit.resetPasswordInitSuccess,
    resetPasswordInitFailure: resetInit.resetPasswordInitFailure,
    resetResetPasswordInit: resetInit.resetResetPasswordInit,

    // Finish operations
    resetPasswordFinish: resetFinish.resetPasswordFinish,
    isResetPasswordFinishPending: resetFinish.isResetPasswordFinishPending,
    resetPasswordFinishError: resetFinish.resetPasswordFinishError,
    resetPasswordFinishSuccess: resetFinish.resetPasswordFinishSuccess,
    resetPasswordFinishFailure: resetFinish.resetPasswordFinishFailure,
    resetResetPasswordFinish: resetFinish.resetResetPasswordFinish,

    // Combined loading state
    isLoading: resetInit.isResetPasswordInitPending || resetFinish.isResetPasswordFinishPending,
  };
}

export function useIgnoreOnboarding() {
  const queryClient = useQueryClient();

  const {mutateAsync, isPending, error, isSuccess} = useMutation({
    async mutationFn() {
      await accountApiService.ignoreOnboarding();
      queryClient.setQueryData(accountKeysStore.onboardingStatus(), "FINISHED");
      queryClient.invalidateQueries({
        queryKey: accountKeysStore.account(),
      });
    },
  });

  return {
    ignoreOnboarding: mutateAsync,
    isIgnoringOnboarding: isPending,
    ignoreOnboardingError: error,
    ignoreOnboardingSuccess: isSuccess,
  };
}
